/**
 * Каркас приложения.
 *
 * Слева постоянная панель проекта, сверху — узкая полоса с переключателем
 * проекта и действиями. Панель темнее полотна: она обрамляет работу, а не
 * соперничает с ней.
 *
 * На узком экране панель превращается в выдвижную, но остаётся полностью
 * доступной (документ «Структура интерфейса», §21).
 */

import { EyeIcon, MenuIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import type { ItemType, ProjectDto, SessionDto } from '@project-os/contracts';
import { BrandMark } from '@/components/common/brand';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { QuickAddDialog } from '@/features/items/QuickAddDialog';
import { CommandPalette } from './CommandPalette';
import { ViewerModeProvider, useViewerMode } from '@/features/project/viewer-mode';
import { CommandPaletteProvider, useCommandPalette } from './command-palette-context';
import { HeaderSearch } from './HeaderSearch';
import { NotificationBell } from './NotificationBell';
import { ProjectSidebar } from './ProjectSidebar';
import { ProjectSwitcher } from './ProjectSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';

function Logo() {
  return (
    <Link
      to="/projects"
      className="flex shrink-0 items-center gap-2 rounded-md px-1.5 py-1 text-sm font-semibold transition-colors hover:bg-accent"
    >
      {/* Знак принимает цвет текста: в тёмной теме белый, в светлой чёрный.
          Держать две картинки не нужно — и они не разойдутся. */}
      <BrandMark className="size-5" />
      <span className="hidden lg:inline">
        project <span className="font-semibold">OS</span>
      </span>
    </Link>
  );
}

export function AppShell({
  session,
  project,
  children,
}: {
  session: SessionDto;
  project?: ProjectDto;
  children: ReactNode;
}) {
  return (
    <ViewerModeProvider>
      <CommandPaletteProvider>
        <AppShellInner session={session} project={project}>
          {children}
        </AppShellInner>
      </CommandPaletteProvider>
    </ViewerModeProvider>
  );
}

function AppShellInner({
  session,
  project,
  children,
}: {
  session: SessionDto;
  project?: ProjectDto;
  children: ReactNode;
}) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddType, setQuickAddType] = useState<ItemType | undefined>(undefined);
  const navigate = useNavigate();
  const location = useLocation();
  const palette = useCommandPalette();
  const viewerMode = useViewerMode();

  // Права проходят через гостевой просмотр: в нём шапка тоже должна
  // выглядеть так, как её видит наблюдатель.
  const canWrite = (project ? viewerMode.filter(project.permissions) : []).includes('ITEM_WRITE');

  const openQuickAdd = (type?: string) => {
    setQuickAddType(type as ItemType | undefined);
    setQuickAddOpen(true);
  };

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-background">
      <header className="flex h-11 shrink-0 items-center gap-1.5 px-2.5">
        {project ? (
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 md:hidden"
                aria-label="Разделы проекта"
              >
                <MenuIcon className="size-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 border-none bg-sidebar p-0">
              <SheetTitle className="sr-only">Разделы проекта</SheetTitle>
              <ProjectSidebar project={project} onNavigate={() => setMobileNavOpen(false)} />
            </SheetContent>
          </Sheet>
        ) : null}

        <Logo />
        <span aria-hidden className="px-0.5 text-muted-foreground/50 select-none">
          /
        </span>
        <ProjectSwitcher currentProjectId={project?.id} />

        <div className="flex-1" />

        {project ? <HeaderSearch project={project} /> : null}

        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2 text-muted-foreground md:hidden"
          onClick={() => palette.open()}
          aria-label="Поиск и действия"
        >
          <SearchIcon className="size-3.5" />
        </Button>

        {canWrite ? (
          <Button size="sm" className="h-7" onClick={() => openQuickAdd()}>
            <PlusIcon />
            <span className="hidden sm:inline">Добавить</span>
          </Button>
        ) : null}

        {project ? (
          <NotificationBell
            projectId={project.id}
            onOpenItem={(itemId) => void navigate(`/projects/${project.id}/items?item=${itemId}`)}
          />
        ) : null}

        <ThemeToggle />
        <UserMenu user={session.user} viewerMode={project ? viewerMode : undefined} />
      </header>

      {/* Гостевой просмотр обязан быть виден.
          Скрытые кнопки правки неотличимы от потерянных прав, и человек
          пойдёт разбираться к администратору вместо того, чтобы выключить
          режим. Полоса узкая и с выходом в один щелчок. */}
      {project && viewerMode.active ? (
        <div className="flex items-center justify-center gap-3 border-b border-tone-warning-border bg-tone-warning-bg px-3 py-1 text-2xs text-tone-warning">
          <span className="flex items-center gap-1.5">
            <EyeIcon className="size-3.5" />
            Гостевой просмотр: показано то же, что видит участник с ролью Viewer. Права не изменены.
          </span>
          <button
            type="button"
            onClick={viewerMode.toggle}
            className="font-medium underline underline-offset-2"
          >
            Выйти
          </button>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        {project ? (
          <aside className="hidden w-60 shrink-0 bg-sidebar md:block">
            <div className="h-full overflow-y-auto">
              <ProjectSidebar project={project} />
            </div>
          </aside>
        ) : null}

        {/* Полотно скруглено сверху слева — панель обрамляет содержание. */}
        <main
          className={
            project
              ? 'min-w-0 flex-1 overflow-y-auto border-border/60 bg-background md:rounded-tl-xl md:border-t md:border-l'
              : 'min-w-0 flex-1 overflow-y-auto bg-background'
          }
        >
          {/* key по адресу перезапускает появление при каждом переходе.
              Анимируется внутренняя обёртка, а не сам main: у main своя
              прокрутка, и смена ключа на нём сбрасывала бы её положение
              вместе с рамкой полотна.
              120 мс и четыре пикселя — этого хватает, чтобы глаз понял, что
              содержимое сменилось, и не хватает, чтобы это ощущалось
              загрузкой. */}
          <div key={location.pathname} className="page-enter flex h-full flex-col">
            {children}
          </div>
        </main>
      </div>

      <CommandPalette project={project} onQuickAdd={openQuickAdd} />

      {project ? (
        <QuickAddDialog
          open={quickAddOpen}
          onOpenChange={setQuickAddOpen}
          projectId={project.id}
          defaultType={quickAddType}
          // Созданный элемент сразу открывается карточкой в списке.
          onCreated={(itemId) => void navigate(`/projects/${project.id}/items?item=${itemId}`)}
        />
      ) : null}
    </div>
  );
}
